import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { SUPABASE_SYNC_DEBUG } from "@/lib/dataRuntimeFlags";
import { recordRealtimeMetric } from "@/lib/queryObservability";
import {
  WarehousePageService,
  type PageResponse,
  type WarehouseAllocationsPageQuery,
  type WarehouseHubJobsPageQuery,
  type WarehouseHubLoadoutPageQuery,
  type WarehouseHubStagingPageQuery,
  type WarehouseHubJobRow,
  type WarehouseHubLoadoutRow,
  type WarehouseHubStagingRow,
  type WarehouseHubSummary,
  type WarehouseJobMaterialAllocationRow,
  type WarehousePendingSkuApprovalRow,
  type WarehouseReadinessPageQuery,
  type WarehouseReceivingPageQuery,
  type WarehouseShipmentsPageQuery,
  type WarehouseStockPageQuery,
  type WarehouseSummary,
  type WarehouseJobReadinessRow,
} from "@/services/warehouse-page.service";
import type { JobAllocation, ReceivingRecord, Shipment, StockRecord } from "@/data/WarehouseInventoryContext";

function emptyPage<T>(): PageResponse<T> {
  return { data: [], count: 0, error: null };
}

function useWarehousePage<T>(
  enabled: boolean | undefined,
  deps: readonly unknown[],
  loader: () => Promise<PageResponse<T>>,
) {
  const [state, setState] = useState<PageResponse<T>>(emptyPage<T>());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setState(emptyPage<T>());
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void loader().then((next) => {
      if (cancelled) return;
      setState(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, deps);

  return { ...state, loading };
}

export function useWarehouseShipmentsPage(query: WarehouseShipmentsPageQuery & { enabled?: boolean }) {
  const result = useWarehousePage<Shipment>(
    query.enabled,
    [query.enabled, query.bucket, query.page, query.pageSize, query.search, query.refreshToken],
    () => WarehousePageService.getShipmentsPage(query),
  );
  return { shipments: result.data, count: result.count, error: result.error, loading: result.loading };
}

export function useWarehouseReceivingPage(query: WarehouseReceivingPageQuery & { enabled?: boolean }) {
  const result = useWarehousePage<ReceivingRecord>(
    query.enabled,
    [query.enabled, query.lane, query.page, query.pageSize, query.search, query.refreshToken],
    () => WarehousePageService.getReceivingPage(query),
  );
  return { records: result.data, count: result.count, error: result.error, loading: result.loading };
}

export function useWarehouseAllocationsPage(query: WarehouseAllocationsPageQuery & { enabled?: boolean }) {
  const result = useWarehousePage<JobAllocation>(
    query.enabled,
    [query.enabled, query.lane, query.page, query.pageSize, query.search, query.refreshToken],
    () => WarehousePageService.getAllocationsPage(query),
  );
  return { allocations: result.data, count: result.count, error: result.error, loading: result.loading };
}

export function useWarehouseStockPage(query: WarehouseStockPageQuery & { enabled?: boolean }) {
  const result = useWarehousePage<StockRecord>(
    query.enabled,
    [query.enabled, query.lane, query.page, query.pageSize, query.search, query.refreshToken],
    () => WarehousePageService.getStockPage(query),
  );
  return { stock: result.data, count: result.count, error: result.error, loading: result.loading };
}

export function useWarehouseJobMaterialAllocationPage(query: {
  enabled?: boolean;
  page: number;
  pageSize: number;
  search?: string;
  filter?: "required" | "available" | "missing" | "allocated";
  refreshToken?: number;
}) {
  const result = useWarehousePage<WarehouseJobMaterialAllocationRow>(
    query.enabled,
    [query.enabled, query.page, query.pageSize, query.search, query.filter, query.refreshToken],
    () => WarehousePageService.getJobMaterialAllocationPage(query),
  );
  return { rows: result.data, count: result.count, error: result.error, loading: result.loading };
}

export function useWarehouseReadinessPage(query: WarehouseReadinessPageQuery & { enabled?: boolean }) {
  const result = useWarehousePage<WarehouseJobReadinessRow>(
    query.enabled,
    [query.enabled, query.lane, query.page, query.pageSize, query.search, query.refreshToken],
    () => WarehousePageService.getReadinessPage(query),
  );
  return { rows: result.data, count: result.count, error: result.error, loading: result.loading };
}

export function useWarehousePendingSkuApprovalsPage(query: {
  enabled?: boolean;
  page: number;
  pageSize: number;
  search?: string;
  refreshToken?: number;
}) {
  const result = useWarehousePage<WarehousePendingSkuApprovalRow>(
    query.enabled,
    [query.enabled, query.page, query.pageSize, query.search, query.refreshToken],
    () => WarehousePageService.getPendingSkuApprovalsPage(query),
  );
  return { approvals: result.data, count: result.count, error: result.error, loading: result.loading };
}

export function useWarehouseHubJobsPage(query: WarehouseHubJobsPageQuery & { enabled?: boolean }) {
  const result = useWarehousePage<WarehouseHubJobRow>(
    query.enabled,
    [query.enabled, query.lane, query.page, query.pageSize, query.search, query.refreshToken],
    () => WarehousePageService.getHubJobsPage(query),
  );
  return { jobs: result.data, count: result.count, error: result.error, loading: result.loading };
}

export function useWarehouseHubStagingPage(query: WarehouseHubStagingPageQuery & { enabled?: boolean }) {
  const result = useWarehousePage<WarehouseHubStagingRow>(
    query.enabled,
    [query.enabled, query.lane, query.page, query.pageSize, query.search, query.refreshToken],
    () => WarehousePageService.getHubStagingPage(query),
  );
  return { rows: result.data, count: result.count, error: result.error, loading: result.loading };
}

export function useWarehouseHubLoadoutPage(query: WarehouseHubLoadoutPageQuery & { enabled?: boolean }) {
  const result = useWarehousePage<WarehouseHubLoadoutRow>(
    query.enabled,
    [query.enabled, query.lane, query.page, query.pageSize, query.search, query.refreshToken],
    () => WarehousePageService.getHubLoadoutPage(query),
  );
  return { rows: result.data, count: result.count, error: result.error, loading: result.loading };
}

export function useWarehouseSummary(filters: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<WarehouseSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!filters.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void WarehousePageService.getSummary(filters.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [filters.enabled, filters.refreshToken]);

  return { data, error, loading };
}

export function useWarehouseHubSummary(filters: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<WarehouseHubSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!filters.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void WarehousePageService.getHubSummary(filters.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [filters.enabled, filters.refreshToken]);

  return { data, error, loading };
}

export function useWarehouseRealtimeRefreshSignal(enabled: boolean) {
  const [refreshToken, setRefreshToken] = useState(0);
  const channelNameRef = useRef(`warehouse-server-page-sync-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!enabled) return;
    const bump = (table: string, payload: { eventType?: string; new?: { id?: string | null }; old?: { id?: string | null } }) => {
      const entityId = String(payload.new?.id ?? payload.old?.id ?? "");
      recordRealtimeMetric({
        source: "warehouse-server-page",
        event: "exact-refetch",
        table,
        entityId: entityId || undefined,
        detail: SUPABASE_SYNC_DEBUG ? { eventType: payload.eventType } : undefined,
      });
      setRefreshToken((current) => current + 1);
    };

    const channel = supabase
      .channel(channelNameRef.current)
      .on("postgres_changes", { event: "*", schema: "public", table: "shipments" }, (payload) => bump("shipments", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "jobs" }, (payload) => bump("jobs", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "receiving_records" }, (payload) => bump("receiving_records", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "job_allocations" }, (payload) => bump("job_allocations", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "job_materials" }, (payload) => bump("job_materials", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "product_registry" }, (payload) => bump("product_registry", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "warehouse_sku_reviews" }, (payload) => bump("warehouse_sku_reviews", payload as any))
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled]);

  return refreshToken;
}
